import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { GetPaymentBatchDetailsResponse } from "features/stock-ordering/core/domain/payment.model";
import {
  GetPaymentBatchDetailsRepository,
  GetPaymentBatchDetailsRepoResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetPaymentBatchDetailsState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetPaymentBatchDetailsState;
  message: string;
  data: GetPaymentBatchDetailsResponse | undefined;
}

const initialState: InitialState = {
  status: GetPaymentBatchDetailsState.initial,
  message: "",
  data: undefined,
};

export const getPaymentBatchDetails = createAsyncThunk(
  "getPaymentBatchDetails",
  async (batchId: number, { rejectWithValue }) => {
    try {
      const response: GetPaymentBatchDetailsRepoResponse =
        await GetPaymentBatchDetailsRepository(batchId);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) throw error;
        throw rejectWithValue(error.response.data.message);
      }
    }
  },
);

export const getPaymentBatchDetailsSlice = createSlice({
  name: "getPaymentBatchDetails",
  initialState,
  reducers: {
    resetGetPaymentBatchDetails: (state) => {
      state.status = GetPaymentBatchDetailsState.initial;
      state.data = undefined;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentBatchDetails.pending, (state) => {
        state.status = GetPaymentBatchDetailsState.inProgress;
      })
      .addCase(getPaymentBatchDetails.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetPaymentBatchDetailsState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(getPaymentBatchDetails.rejected, (state, action) => {
        state.status = GetPaymentBatchDetailsState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetPaymentBatchDetails = (state: RootState) =>
  state.getPaymentBatchDetails;
export const { resetGetPaymentBatchDetails } =
  getPaymentBatchDetailsSlice.actions;
export default getPaymentBatchDetailsSlice.reducer;
